import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { Observable } from 'rxjs'
import { RolesType } from 'src/enums/roles.enum'
import { environment } from 'src/environments/environment'
import { FilesUtils } from 'src/helpers/files.helper'
import { IFiles } from 'src/interfaces/files/files.interface'
import { IPagination } from 'src/interfaces/pagination/pagination.interface'

export interface IUserOptions {
  displayName: string
  id: string
}

export interface IEligibleChildrenRoles {
  roles: RolesType[]
  user: IUserOptions
}

export interface ListUsersParams extends IPagination {
  role?: RolesType
}

export interface UpdateUserParents {
  roles: RolesType[]
  parentId: string
  userIds: string[]
}

@Injectable({
  providedIn: 'root',
})
export class UserService {
  baseRoute = `${environment.api}/users`

  constructor(
    private http: HttpClient,
    private filesUtils: FilesUtils,
  ) {}

  list(filterData: ListUsersParams): Observable<any> {
    return this.http.get(`${this.baseRoute}`, {
      params: { ...filterData },
    })
  }

  listExternalUsers(filterData: IPagination): Observable<IPagination> {
    return this.http.get<IPagination>(`${this.baseRoute}/external-users`, {
      params: { ...filterData },
    })
  }

  contactList(filterData: IPagination): Observable<any> {
    return this.http.get(`${this.baseRoute}/contact-list`, {
      params: { ...filterData },
    })
  }

  listMySubordinates(filterData: IPagination): Observable<any> {
    return this.http.get(`${this.baseRoute}/me/subordinates`, {
      params: { ...filterData },
    })
  }

  create(data: any): Observable<any> {
    return this.http.post(`${this.baseRoute}/create`, data)
  }

  provisionalUserCreate(data: any): Observable<any> {
    return this.http.post(`${this.baseRoute}/provisional-user/create`, data)
  }

  getById(id: string): Observable<any> {
    return this.http.get(`${this.baseRoute}/${id}`)
  }

  edit(id: string, data: any): Observable<any> {
    return this.http.patch(`${this.baseRoute}/update/${id}`, data)
  }

  updateUserProfile(data: any): Observable<any> {
    return this.http.patch(`${this.baseRoute}/update-me`, data)
  }

  editMe(data: any): Observable<any> {
    return this.http.patch(`${this.baseRoute}/me`, data)
  }

  delete(id: string): Observable<any> {
    return this.http.patch(`${this.baseRoute}/delete/${id}`, {})
  }

  uploadFiles(files: IFiles[], userId: string): Observable<any> {
    const formData = this.filesUtils.createFileFormData(files)
    return this.http.post(
      `${environment.api}/storage/upload/users/${userId}`,
      formData,
    )
  }

  deleteFiles(files: any[], userId: string): Observable<any> {
    return this.http.post(`${environment.api}/storage/delete/users/${userId}`, {
      files,
    })
  }

  listAll(): Observable<any> {
    return this.http.get(`${this.baseRoute}/list-all`)
  }

  listIntegrators(filterData: IPagination): Observable<IPagination> {
    return this.http.get<IPagination>(`${this.baseRoute}/integrators`, {
      params: { ...filterData },
    })
  }

  getEligibleParents(role: RolesType): Observable<IUserOptions[]> {
    return this.http.get<IUserOptions[]>(`${this.baseRoute}/eligible-parents`, {
      params: { userRole: role },
    })
  }

  getEligibleChildrenRoles(userId: string): Observable<IEligibleChildrenRoles> {
    return this.http.get<IEligibleChildrenRoles>(
      `${this.baseRoute}/eligible-children-roles`,
      { params: { userId } },
    )
  }

  updateUsersParent(data: UpdateUserParents): Observable<any> {
    return this.http.patch(`${this.baseRoute}/update-many-user-parents`, data)
  }
}
